import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { IconButton } from '@/design-system'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { WizardPrintSlot } from '../../types'
import { StepLead } from '../StepCopy'

export interface PrintSlotBoardProps {
  slots: WizardPrintSlot[]
  onToggleExclude: (index: number) => void
  onMoveLeft: (index: number) => void
  onMoveRight: (index: number) => void
}

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
  gap: 12,
})

const Slot = styled('button', {
  shouldForwardProp: (p) => p !== '$empty' && p !== '$excluded',
})<{ $empty?: boolean; $excluded?: boolean }>(
  ({ $empty, $excluded }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    aspectRatio: '1 / 1',
    borderRadius: 16,
    overflow: 'hidden',
    cursor: $empty ? 'default' : 'pointer',
    position: 'relative',
    width: '100%',
    background: $empty ? 'transparent' : 'var(--ds-hover)',
    border: $empty ? '1.5px dashed var(--ds-border)' : '1px solid var(--ds-border)',
    opacity: $excluded ? 0.4 : 1,
    transition: [
      'opacity var(--ds-duration-fast) var(--ds-ease)',
      'transform var(--ds-duration-normal) var(--ds-ease)',
    ].join(', '),
    '&:hover:not(:disabled)': $empty
      ? undefined
      : { transform: 'scale(1.02)' },
    '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    '&:focus-visible': { outline: '2px solid var(--ds-ink)', outlineOffset: 2 },
  }),
)

const Badge = styled(Box)({
  position: 'absolute',
  left: 8,
  top: 8,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: '#fff',
  background: 'rgba(0,0,0,0.55)',
  borderRadius: 8,
  padding: '3px 7px',
  lineHeight: 1.2,
})

const ExcludedTag = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.55)',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ds-ink)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
})

export function PrintSlotBoard({
  slots,
  onToggleExclude,
  onMoveLeft,
  onMoveRight,
}: PrintSlotBoardProps) {
  const placed = slots.filter((s) => s.photoId && !s.excluded).length
  const totalFilled = slots.filter((s) => s.photoId).length

  return (
    <Box>
      <StepLead>
        Album photos auto-fill this room’s print slots. Tap to exclude · arrows to reorder.
      </StepLead>

      <Box
        display='flex'
        alignItems='baseline'
        justifyContent='space-between'
        mb={2.5}
        gap={2}
      >
        <Typography sx={{ fontSize: 12, color: 'var(--ds-ink-subtle)', lineHeight: 1.4 }}>
          Empty slots stay open for the editor.
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--ds-ink-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          {placed} of {slots.length} slots
          {totalFilled < slots.length ? ` · ${totalFilled} photos` : ''}
        </Typography>
      </Box>

      <Grid>
        {slots.map((slot) => (
          <Box key={slot.index}>
            <Slot
              type='button'
              $empty={!slot.photoId}
              $excluded={slot.excluded}
              disabled={!slot.photoId}
              onClick={() => slot.photoId && onToggleExclude(slot.index)}
              aria-label={
                slot.photoId
                  ? slot.excluded
                    ? `Include print ${slot.index + 1}`
                    : `Exclude print ${slot.index + 1}`
                  : `Empty slot ${slot.index + 1}`
              }
            >
              {(slot.thumbUrl || slot.url) && (
                <img src={slot.thumbUrl || slot.url} alt='' />
              )}
              <Badge>{slot.index + 1}</Badge>
              {slot.excluded && slot.photoId && <ExcludedTag>Off</ExcludedTag>}
            </Slot>

            {slot.photoId && (
              <Box display='flex' justifyContent='center' gap={0.25} mt={0.75}>
                <IconButton
                  tone='quiet'
                  size='small'
                  aria-label='Move left'
                  disabled={slot.index === 0}
                  onClick={() => onMoveLeft(slot.index)}
                  sx={{ width: 28, height: 28 }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  tone='quiet'
                  size='small'
                  aria-label='Move right'
                  disabled={slot.index === slots.length - 1}
                  onClick={() => onMoveRight(slot.index)}
                  sx={{ width: 28, height: 28 }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Grid>
    </Box>
  )
}
